import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { css, Global } from '@emotion/react';
import { rgba } from 'polished';
import { up, between } from '../../core/utils/Breakpoints';
import { layout } from '../../core/styles/theme/layout';
import { colors } from '../../core/styles/theme/default';
import Container from '../Container';
import { Grid, GridItem } from './Grid';

const StyledContainer = styled(Container)`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 99999999;
  pointer-events: none;

  ${layout.map((definition, index, layouts) => {
    return `.${definition.name} {
			${
        index + 1 < layouts.length
          ? between(`${definition.name}`, `${layouts[index + 1].name}`)
          : up(`${definition.name}`)
      } {
				display: flex;
			}
		}`;
  })}
`;

const StyledGrid = styled(Grid)`
  height: 100%;
  display: none;
  flex-wrap: nowrap;
`;

const StyledGridItem = styled(GridItem)`
  height: 100%;
  background: ${rgba(colors.secondary, 0.15)};
  outline: 0 !important;
`;

const renderGridItems = (gridColumns: any) => {
  // Create an array with as many elements as columns
  return [...Array(gridColumns)].map((columnElement, index) => (
    <StyledGridItem key={`gridItem-${gridColumns}-${-index}`} size={1} />
  ));
};

const renderGrids = layout.map((layoutDefinition: any) => (
  <StyledGrid
    key={layoutDefinition.name}
    columns={layoutDefinition.gridColumns}
    className={layoutDefinition.name}
    // Add all elements in the layout as data attributes for easy debugging in the inspector
    {...Object.keys(layoutDefinition).reduce((acc: any, curr: any) => {
      acc[`data-${curr.toLowerCase()}`] = layoutDefinition[curr];
      return acc;
    }, {})}
    // Add all elements in the layout as props for easy debugging in zReact tools
    {...layoutDefinition}
  >
    {renderGridItems(layoutDefinition.gridColumns)}
  </StyledGrid>
));

const GridPreview = () => {
  const [isGridVisible, setIsGridVisible] = useState(
    (window as any).parent.isGridVisible,
  );

  const toggleGrid = (event: any) => {
    if (event.key === 'showGrid') {
      let localStorageGridValue;
      try {
        localStorageGridValue = JSON.parse(event.newValue);
      } catch (error) {
        localStorageGridValue = false;
      }
      setIsGridVisible(localStorageGridValue);
    } else if ('detail' in event && 'isGridVisible' in event.detail) {
      setIsGridVisible(event.detail.isGridVisible);
    }
  };

  const shouldRenderGrid = () =>
    isGridVisible || localStorage.getItem('showGrid') === 'true';

  useEffect(() => {
    window.parent.addEventListener('toggleGrid', toggleGrid, false);
    window.addEventListener('storage', toggleGrid, false);

    return () => {
      window.parent.removeEventListener('toggleGrid', toggleGrid, false);
      window.removeEventListener('storage', toggleGrid, false);
    };
  }, []);

  return shouldRenderGrid() ? (
    <>
      <Global
        styles={css`
          div[class*='StyledGridItem'] {
            outline: 1px solid ${colors.secondary};
          }
        `}
      />
      <StyledContainer>{renderGrids}</StyledContainer>
    </>
  ) : null;
};

export default GridPreview;
